import { MemberMarkers } from "@prototypes/member-markers/MemberMarkers";
import { Link } from "react-router-dom";
import { RequestStatusMap } from "../../common/types/RequestStatus";
import { formatMemberName } from "../../utils/name.util";
import { formatIsoDateTimeForChatMessage } from "../../utils/time.util";
import { compareToIsoDateTime } from "../../utils/timestamp.util";
import { MemberAvatar } from "../Common/Avatar";
import MessageDescription from "./MessageDescription";
import "./MessageListItem.css";
import type {
    RecentConversation,
    RecentConversationMember,
    RecentConversationRequest,
} from "@albertine/types/openapi/lmt";

function conversationHasReactionToShow(conversation: RecentConversation): boolean {
    const { memberLastReactionTimestamp, agentUnseenReactionsCount, lastMessage } =
        conversation;
    if (!lastMessage || !memberLastReactionTimestamp || agentUnseenReactionsCount > 0)
        return false;

    return compareToIsoDateTime(memberLastReactionTimestamp, lastMessage.sentAt) > 0;
}

function reactionText(
    reaction: string | undefined | null,
    member: RecentConversationMember,
): string {
    return `${member.firstName} ${member.lastName} reacted ${reaction} to: `;
}

function LastMessageOrReaction(props: {
    conversation: RecentConversation;
    member: RecentConversationMember;
}) {
    const { conversation, member } = props;
    const {
        lastMessage,
        lastMessageMemberReactedTo,
        agentLastUnseenReactionMessageId,
    } = conversation;
    const showReaction = conversationHasReactionToShow(conversation);
    const message = showReaction ? lastMessageMemberReactedTo : lastMessage;
    const linkToMember = `/member/${member.id}`;
    const linkMessageId =
        showReaction && agentLastUnseenReactionMessageId
            ? `/message/${agentLastUnseenReactionMessageId}`
            : "";
    const linkToChat = message?.requestId
        ? `/request/${message?.requestId}${linkMessageId}`
        : `${linkToMember}${linkMessageId}`;

    const linkContent = showReaction ? (
        <span>{reactionText(message?.memberReaction, member)}</span>
    ) : (
        lastMessage &&
        lastMessage.sentBy !== member.id && (
            <span>{`${lastMessage?.senderName}: `}</span>
        )
    );
    return (
        <Link to={linkToChat} className="message-list-item__message clear-link">
            {linkContent}
            <MessageDescription message={message} quoted={showReaction} />
        </Link>
    );
}

type Props = {
    conversation: RecentConversation;
    member: RecentConversationMember;
    requests: RecentConversationRequest[];
};

const MessageListItem = function MessageListItem(props: Props) {
    const { conversation, member, requests } = props;

    // TODO Fix some react state inconsistency that requires this
    if (!member || !conversation) return <div />;

    const linkToMember = `/member/${member.id}`;

    return (
        <div>
            <div className="message-list-item">
                {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                <Link to={linkToMember} className="message-list-item__image clear-link">
                    <MemberAvatar member={member} size="big" />
                </Link>
                <div className="column">
                    <div className="message-list-item__header column-row">
                        {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                        <div className="flex min-w-0 flex-col gap-0.5">
                            <div className="flex min-w-0 items-center gap-1">
                                <Link
                                    to={linkToMember}
                                    className="clear-link message-list-item__name"
                                >
                                    {formatMemberName(member)}
                                </Link>
                                <MemberMarkers
                                    isTestMember={member.isTestMember}
                                    isVipMember={member.isVipMember}
                                />
                            </div>
                            <span className="text-[var(--a-ds-font-color-tertiary)] text-xs">
                                {member.assignedToAgentName ?? "Unassigned"}
                            </span>
                        </div>
                        {conversation.unreadMessagesCountByAgent != null &&
                        conversation.unreadMessagesCountByAgent > 0 ? (
                            <div className="message-list-item__unread">
                                {conversation.unreadMessagesCountByAgent +
                                    conversation.agentUnseenReactionsCount}
                            </div>
                        ) : (
                            <div className="message-list-item__time">
                                {formatIsoDateTimeForChatMessage(
                                    conversation.lastMessage?.sentAt || null,
                                ) || ""}
                            </div>
                        )}
                    </div>
                    <div className="message-list-item__secondary column-row">
                        <LastMessageOrReaction
                            conversation={conversation}
                            member={member}
                        />
                        <div className="message-list-item__time" />
                    </div>
                    {requests.length > 0 && (
                        <div className="message-list-item__requests">
                            {requests.map((request) => (
                                <Link
                                    key={request.id}
                                    to={`/request/${request.id}`}
                                    className="clear-link message-list-item__request column-row"
                                >
                                    <div>
                                        {request.ticketNumber && (
                                            <span className="message-list-item__request__ticket">{`#${request.ticketNumber}`}</span>
                                        )}
                                        <span>{request.title}</span>
                                    </div>
                                    <div>{RequestStatusMap[request.status]}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessageListItem;
